import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { GuideLinks } from "@/components/GuideLinks";
import { LeadForm } from "@/components/LeadForm";
import { StickyCta } from "@/components/StickyCta";
import { HeroParallax } from "@/components/HeroParallax";
import { cases } from "@/lib/cases-data";
import { CaseCard } from "@/components/CasePreview";
import portraitAsset from "@/assets/nicolai-schnepf-portrait.webp";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Freelancer Deutschland | Nicolai Schnepf";
const DESCRIPTION =
  "Shopify Freelancer in Deutschland: Sie arbeiten direkt mit dem Spezialisten, ohne Agentur dazwischen. Transparente Preise, 110 € netto die Stunde oder Festpreis.";

const services = [
  {
    title: "Shop-Erstellung",
    price: "Festpreis nach Umfang",
    text: "Sie brauchen einen neuen Shop: Theme aufbauen, Produkte einpflegen, Zahlung und Versand einrichten, Rechtstexte anbinden. Am Ende pflegen Sie den Shop selbst weiter.",
    points: ["Theme-Aufbau & Einrichtung", "Produkte, Kategorien, Navigation", "Zahlung, Versand, Steuern"],
    href: "/shopify-shop-erstellen-lassen" as const,
    ask: "Shop-Erstellung",
    cta: "Shop anfragen",
  },
  {
    title: "Anpassungen & Entwicklung",
    price: "110 € / Stunde",
    text: "Sie haben eine konkrete Anpassung: neue Sektion, eigene Funktion, ein Fehler, der stört, eine App-Anbindung. Kleines nach Aufwand, Größeres zum Festpreis.",
    points: ["Liquid & Theme-Entwicklung", "Features und Sektionen nach Maß", "Bugfixes, Speed, App-Integration"],
    href: "/shopify-theme-anpassung" as const,
    ask: "Anpassung",
    cta: "Anpassung anfragen",
  },
  {
    title: "Conversion Optimierung",
    price: "Festpreis ab 3.990 €",
    text: "Sie möchten mehr aus Ihrem bestehenden Shop herausholen. Ich prüfe Produktseiten, Warenkorb, mobile Nutzung und Ladezeiten und setze die wichtigsten Verbesserungen um.",
    points: ["Audit & Schwachstellen finden", "Produktseite, Warenkorb, Checkout", "Umsetzung und Auswertung"],
    href: "/shopify-conversion-optimierung" as const,
    ask: "Conversion-Optimierung",
    cta: "Optimierung anfragen",
  },
];


const forWhom = [
  "Sie starten gerade und brauchen einen Shop, der von Tag eins an läuft",
  "Ihr Shop läuft, aber die To-do-Liste wird länger statt kürzer",
  "Ihr bisheriger Entwickler meldet sich nicht oder ist für Kleinkram zu teuer",
  "Sie möchten einen festen Ansprechpartner, der Shopify wirklich kann",
];

const faqs = [
  {
    q: "Was macht ein Shopify Freelancer?",
    a: "Ein Shopify Freelancer baut, betreut und verbessert Ihren Shop als feste Einzelperson statt als Agentur. Bei mir heißt das: Shop aufsetzen, Theme anpassen, Funktionen entwickeln, Fehler beheben und die Verkaufszahlen verbessern. Sie sprechen dabei immer direkt mit dem, der die Arbeit auch macht.",
  },
  {
    q: "Was kostet ein Shopify Freelancer?",
    a: "Üblich sind in Deutschland, Österreich und der Schweiz 90 bis 140 € netto die Stunde, Agenturen liegen eher bei 120 bis 180 €. Ich arbeite für 110 € netto die Stunde, größere Vorhaben rechne ich zum Festpreis ab.",
  },
  {
    q: "Sind Sie der offizielle Shopify-Support?",
    a: "Nein. Ich bin ein unabhängiger Freelancer und helfe Ihnen beim Aufbau, bei Anpassungen und bei der Optimierung Ihres Shops. Für Fragen zu Abrechnung, Kündigung oder Ihrem Shopify-Konto wenden Sie sich bitte direkt an Shopify unter help.shopify.com.",
  },
  {
    q: "Was kostet eine Stunde Shopify-Entwicklung?",
    a: "110 € netto die Stunde für Anpassungen, Bugfixes und kleinere Aufgaben. Bevor es losgeht, erhalten Sie eine Schätzung, auf der Rechnung steht später keine Überraschung.",
  },
  {
    q: "Was kostet ein kompletter Shopify Shop?",
    a: "Das hängt davon ab, wie viele Produkte Sie haben, was an Funktionen dazu soll und wie individuell das Design werden darf. Nach einem kurzen Gespräch erhalten Sie einen Festpreis, und dabei bleibt es dann auch.",
  },
  {
    q: "Können Sie meinen bestehenden Shop übernehmen?",
    a: "Ja. Ich steige regelmäßig in laufende Projekte ein, übernehme Shops von Vorgängern und arbeite direkt in Ihrem Theme weiter.",
  },
  {
    q: "Wie schnell kann es losgehen?",
    a: "Kleinere Sachen meistens innerhalb weniger Tage. Bei größeren Projekten sage ich Ihnen direkt und ehrlich, wann ich Kapazität habe.",
  },
  {
    q: "Können Sie meinen Shop auch dauerhaft betreuen?",
    a: "Ja, viele buchen mich einfach ein paar Stunden im Monat, für Anpassungen, neue Features und alles, was so aufläuft.",
  },
  {
    q: "Was, wenn ich noch nicht weiß, was ich brauche?",
    a: "Dann schreiben Sie mir einfach, was Sie an Ihrem Shop stört. Ich sage Ihnen, was ich machen würde, was es kostet, und wenn es sich nicht lohnt, sage ich das auch.",
  },
];


export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/b7b26882-c5af-4f99-9379-e0e569f0d952/id-preview-0cf08bc7--7f003aba-e8e6-4ed9-9f4c-cd8fc59634c9.lovable.app-1785492205652.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/b7b26882-c5af-4f99-9379-e0e569f0d952/id-preview-0cf08bc7--7f003aba-e8e6-4ed9-9f4c-cd8fc59634c9.lovable.app-1785492205652.png" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/` },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "Shopify Freelancer",
          description: DESCRIPTION,
          url: SITE,
          areaServed: ["DE", "AT", "CH"],
          knowsAbout: [
            "Shopify Shop erstellen",
            "Shopify Theme Entwicklung",
            "Shopify Conversion Optimierung",
            "Liquid Programmierung",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

function Index() {
  const [service, setService] = useState<string>("");

  function askFor(name: string) {
    setService(name);
    document.getElementById("anfrage")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      <main id="top">
        {/* Hero */}
        <section className="veil relative isolate border-b border-border/60">
          <HeroParallax />
          <div className="mx-auto grid max-w-6xl gap-12 px-5 pb-20 pt-16 md:pb-28 md:pt-24 md:grid-cols-[1.15fr_0.85fr] md:items-center md:gap-16">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Shopify Freelancer · Nicolai Schnepf
              </p>
              <h1 className="mt-6 max-w-3xl text-4xl font-bold leading-[1.06] md:text-[3.4rem]">
                Shopify Freelancer Deutschland:{" "}
                <span className="text-signal">Ihr Shop soll verkaufen, nicht nur gut aussehen.</span>
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
                Sie haben besseres zu tun, als sich mit Theme-Code, Apps und halbfertigen
                Shops herumzuschlagen. Ich bin Nicolai Schnepf, Shopify Freelancer für
                Deutschland, Österreich und die Schweiz. Ich baue und verbessere Ihren Shop,
                Sie kümmern sich um Ihr Geschäft.
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href="#anfrage"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Kostenlose Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-semibold transition-colors hover:border-primary"
                >
                  Kundenprojekte ansehen
                </Link>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Unverbindlich · Antwort innerhalb von 48 Stunden
              </p>

            </div>


            <div className="relative mx-auto w-full max-w-sm md:max-w-none">
              <div className="absolute -inset-3 -z-10 rounded-[2rem] bg-primary/20 blur-3xl" />
              <div className="shadow-lift overflow-hidden rounded-[1.75rem] border border-border bg-card">
                <img
                  src={portraitAsset}
                  alt="Nicolai Schnepf, Shopify Freelancer"
                  width={1000}
                  height={1500}
                  decoding="async"
                  fetchPriority="high"
                  className="aspect-[2/3] w-full object-cover object-top"
                />
              </div>
              <p className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Nicolai Schnepf · Shopify Freelancer · DACH
              </p>
            </div>
          </div>
        </section>

        {/* Referenzzeile */}
        <section className="border-b border-border/60 py-7">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-3 px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
              Shops, mit denen ich arbeite
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold">
              {[
                { n: "Lunu", s: "deinlunu" },
                { n: "kremka", s: "kremka" },
                { n: "MASKABEL", s: "maskabel" },
                { n: "Snyder Golf", s: "snydergolf" },
                { n: "Logo-Matten", s: "logo-matten" },
              ].map((b) => (
                <a key={b.s} href={`/cases#${b.s}`} className="text-foreground/80 hover:text-primary">
                  {b.n}
                </a>
              ))}
            </div>
            <Link
              to="/cases"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary"
            >
              Kundenprojekte ansehen <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* Problem & Einsatz (StoryBrand: Held hat ein Problem, Nichtstun kostet) */}
        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-4xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Kommt Ihnen das bekannt vor?
            </p>
            <h2 className="mt-5 text-3xl font-bold leading-tight md:text-4xl">
              Ihr Shop kostet Sie Zeit, Nerven und jeden Monat Umsatz
            </h2>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {[
                {
                  title: "Die To-do-Liste wächst",
                  text: "Kleine Anpassungen stapeln sich, Ihr Entwickler meldet sich nicht, und Agenturen sind für solche Aufgaben zu teuer.",
                },
                {
                  title: "Besucher kaufen zu wenig",
                  text: "Sie bezahlen für Werbung, die Leute kommen in den Shop, aber bestellen nicht. Das ist, als hätten Sie einen Laden, in den keiner reinkommt.",
                },
                {
                  title: "Jeder Monat zählt",
                  text: "Solange der Shop nicht richtig läuft, gehen Bestellungen an Wettbewerber, deren Shop einfach besser funktioniert.",
                },
              ].map((p) => (
                <div key={p.title} className="rounded-lg border border-border bg-card p-6">
                  <h3 className="text-lg font-semibold">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 max-w-2xl leading-relaxed text-muted-foreground">
              Das muss nicht so bleiben. Mit einem festen Ansprechpartner, der Shopify
              wirklich kann, wird Ihr Shop zu dem, was er sein soll: ein Verkäufer, der
              rund um die Uhr arbeitet.
            </p>
          </div>
        </section>

        {/* Leistungen */}
        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Leistungen
            </p>
            <h2 className="mt-5 max-w-2xl text-3xl font-bold md:text-4xl">
              Was ich als Shopify Freelancer für Sie mache
            </h2>

            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {services.map((s) => (
                <article
                  key={s.title}
                  className="shadow-lift flex flex-col rounded-lg border border-border bg-card p-6"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
                    {s.price}
                  </p>
                  <h3 className="mt-3 text-xl font-semibold">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                  <ul className="mt-5 grid gap-2 border-t border-border/60 pt-5 text-sm">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2.5">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span className="text-muted-foreground">{p}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-2 pt-6">
                    <button
                      type="button"
                      onClick={() => askFor(s.ask)}
                      className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                    >
                      {s.cta} <ArrowUpRight className="h-4 w-4" />
                    </button>
                    <Link to={s.href} className="text-sm font-semibold text-primary">
                      Mehr dazu
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Cases */}
        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="text-3xl font-bold md:text-4xl">Ausgewählte Kundenprojekte</h2>
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary"
              >
                Alle Kundenprojekte ansehen <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-9 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {["kremka", "deinlunu", "gordigear", "snydergolf"]
                .map((slug) => cases.find((study) => study.slug === slug))
                .filter((study): study is (typeof cases)[number] => Boolean(study))
                .map((study) => (
                  <CaseCard key={study.slug} study={study} />
                ))}
            </div>
          </div>
        </section>

        {/* Anfrage weit oben */}
        <section id="anfrage" data-lead-block className="veil border-b border-border/60 py-16">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">
                Sagen Sie mir kurz, was ansteht
              </h2>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Sie erhalten innerhalb von 48 Stunden eine kurze Einschätzung zu Ihrem
                Vorhaben, den empfohlenen nächsten Schritt und, soweit anhand Ihrer Angaben
                möglich, einen groben Kostenrahmen. Kostenlos, unverbindlich und ohne
                Verkaufsgespräch. Auch wenn Sie noch keinen Shop haben.
              </p>
            </div>
            <LeadForm
              key={service || "default"}
              source="startseite-oben"
              compact
              showServices
              defaultService={service || undefined}
              cta="Kostenlose Einschätzung erhalten"
            />
          </div>
        </section>



        {/* Für wen */}
        <section className="border-b border-border/60 py-20">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 md:grid-cols-[1fr_1.1fr]">
            <div>
              <h2 className="text-3xl font-bold leading-tight md:text-4xl">
                Sie brauchen niemanden, der Ihnen Shopify erklärt. Sie brauchen jemanden, der es macht.
              </h2>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Ich weiß, wie ärgerlich das ist: Sie wollen verkaufen, stattdessen beschäftigen
                Sie sich mit Technik. Genau dafür arbeiten Shops wie Lunu, kremka und
                Snyder Golf seit Jahren mit mir. Sie sagen mir, was ansteht. Ich sage Ihnen,
                was es kostet und wann es fertig ist. Und dann setze ich es um.
              </p>

            </div>
            <ul className="space-y-px overflow-hidden rounded-lg border border-border">
              {forWhom.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 border-b border-border bg-card px-5 py-4 text-sm last:border-b-0 md:text-base"
                >
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Conversion-Teaser */}
        <section className="veil border-b border-border/60 py-20">
          <div className="mx-auto max-w-4xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Meine Spezialität
            </p>
            <h2 className="mt-5 text-3xl font-bold leading-tight md:text-4xl">
              Ihr Shop läuft, aber verkauft er auch das, was er könnte?
            </h2>
            <p className="mt-5 max-w-2xl leading-relaxed text-muted-foreground">
              Besucher kommen in Ihren Shop, aber zu wenige bestellen? Ich prüfe
              Produktseiten, Warenkorb, mobile Nutzung und Ladezeiten, finde die Stellen,
              an denen Kunden abspringen, und setze die wichtigsten Verbesserungen um,
              ohne dass Sie mehr für Werbung ausgeben. Festpreis ab 3.990 € netto.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/shopify-conversion-optimierung"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Conversion Optimierung ansehen <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/roi-rechner"
                className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-semibold transition-colors hover:border-primary"
              >
                Umsatzpotenzial berechnen
              </Link>
            </div>
          </div>
        </section>

        {/* Ablauf */}
        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              So läuft's ab
            </p>
            <h2 className="mt-5 text-3xl font-bold md:text-4xl">
              In drei Schritten zum Ergebnis
            </h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
              {
                step: "01",
                title: "Sie schreiben mir",
                text: "Zwei Sätze, was ansteht, dazu Ihre Shop-URL, falls es den Shop schon gibt. Mehr brauche ich fürs Erste nicht.",
              },
              {
                step: "02",
                title: "Sie erhalten eine klare Antwort",
                text: "Innerhalb von 48 Stunden: was ich machen würde, was es kostet, wann es fertig ist. Und wenn es sich für Sie nicht lohnt, sage ich das genauso.",
              },
              {
                step: "03",
                title: "Ich setze um",
                text: "Sie erhalten Bescheid, wenn etwas live geht, ohne dass Sie hinterhertelefonieren müssen.",
              },
              ].map((s) => (
                <div key={s.step} className="rounded-lg border border-border bg-card p-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
                    {s.step}
                  </p>
                  <h3 className="mt-3 text-lg font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Fragen, die oft kommen</h2>
            <div className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border">
              {faqs.map((f) => (
                <div key={f.q} className="bg-card p-6">
                  <h3 className="text-base font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Kontakt */}
        <section id="kontakt" data-lead-block className="py-20 pb-28 lg:pb-20">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">
                Stellen Sie sich vor, Ihr Shop läuft einfach
              </h2>
              <p className="mt-5 text-muted-foreground">
                Keine offenen Baustellen mehr, keine Technik, die Sie aufhält, sondern ein
                Shop, der Besucher in Käufer verwandelt. Der erste Schritt dorthin kostet
                Sie zwei Minuten: Schreiben Sie mir, worum es geht. Sie erhalten innerhalb
                von 48 Stunden eine ehrliche <span className="text-signal">Ersteinschätzung</span>,
                den empfohlenen nächsten Schritt und, soweit möglich, einen groben
                Kostenrahmen. Kein Verkaufsgespräch, keine Verpflichtung.
              </p>
            </div>
            <LeadForm
              source="startseite-footer"
              compact={false}
              showServices
              cta="Kostenlose Einschätzung erhalten"
            />

          </div>
        </section>

      </main>

      <StickyCta targetId="anfrage" />
      <GuideLinks
        links={[
        { to: "/shopify-conversion-optimierung", label: "Conversion-Optimierung", text: "Wie aus vorhandenen Besuchern mehr Bestellungen werden." },
        { to: "/blog/shopify-seo-optimierung-guide", label: "Shopify SEO Optimierung", text: "Struktur, Technik und Inhalte in der Reihenfolge, die wirkt." },
        { to: "/blog/shopify-checkout-optimieren", label: "Checkout optimieren", text: "Die haeufigsten Abbruchgruende im Kaufabschluss." },
        { to: "/ratgeber/shopify-kosten", label: "Was kostet ein Shopify Shop", text: "Realistische Preisspannen fuer Aufbau und Betrieb." },
        { to: "/shopify-app-entwicklung", label: "Shopify App Entwicklung", text: "Wann eine eigene App noetig ist und wann das Theme reicht." },
        { to: "/cases", label: "Kundenprojekte", text: "Ausgewaehlte Shopify-Projekte aus der Praxis." },
        ]}
      />
      <SiteFooter />
    </div>
  );
}
